import { PageViewer, cleanPage, types, useAdminContext, useReactBricksContext } from "react-bricks/frontend"
import { useQueryParams } from "../../shared/hooks/use-url-search-params"
import React, { useMemo, useEffect, useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useForm, type SubmitHandler } from "react-hook-form"

import { cn } from "../../shared/utils/cn"
import { usePageContext } from "../../context/page"
import WrapWithError from "../../shared/components/fields/wrap-with-error"
import { Link } from "../../shared/utils/router"
import { usePostCart, useQueryCart } from "../../shared/hooks/cart/use-query-cart"
import { errorToast } from "../../shared/utils/error-toast"
import { setCookie, deleteCookie, getCookie } from "../../shared/utils/cookie"

type FormValues = {
    code: string
}

const Lock: types.Brick = () => {
    const { t } = useTranslation("translations")

    const { pageTypes, bricks } = useReactBricksContext()
    const { page } = usePageContext()
    const pageOk = useMemo(() => cleanPage(page, pageTypes, bricks), [page, pageTypes, bricks])

    const { isAdmin, previewMode } = useAdminContext()

    const { data: cartData, isLoading } = useQueryCart()

    const cartCode = useMemo(() => cartData?.voucher_trk, [cartData?.voucher_trk])

    const filteredPage = useMemo(() => {
        const withoutLock = pageOk.content.filter(brick => brick.type !== "Lock")
        return {
            ...pageOk,
            content: cartCode ? withoutLock : withoutLock.slice(0, 1),
        }
    }, [pageOk, isAdmin, previewMode, cartCode])

    const {
        formState: { errors },
        handleSubmit,
        register,
    } = useForm<FormValues>({
        mode: "onTouched",
        defaultValues: {
            code: "",
        },
        context: undefined,
        criteriaMode: "firstError",
        shouldFocusError: true,
    })

    const { mutateAsync } = usePostCart()

    const { getQueryParam, removeQueryParam } = useQueryParams()
    const code = getQueryParam("code")

    const attemptedCode = useRef<string>()

    const checkCode = useCallback(
        async (activeCode: string) => {
            if (attemptedCode.current === activeCode) return
            attemptedCode.current = activeCode

            const body = {
                voucher_trk: activeCode,
            }

            if (code) removeQueryParam("code")

            try {
                await mutateAsync(body)
                setCookie("voucher_trk", activeCode, { maxAge: 60 * 60 * 24 * 14 })
            } catch (error) {
                errorToast(error)
                deleteCookie("voucher_trk")
            }
        },
        [mutateAsync, removeQueryParam, code]
    )

    useEffect(() => {
        if (!code) return

        checkCode(code)
    }, [code, checkCode])

    useEffect(() => {
        const cookieCode = getCookie("voucher_trk")
        if (!cookieCode || code || attemptedCode.current || isLoading || cartCode) return

        checkCode(cookieCode)
    }, [code, checkCode, isLoading, cartCode])

    const submit: SubmitHandler<FormValues> = async values => {
        attemptedCode.current = undefined
        checkCode(values.code)
    }

    const error = errors.code

    return (
        <div className="min-h-screen-w/o-header relative" style={isAdmin ? { "--header-height": "0px" } : undefined}>
            {!cartCode && (
                <div className="z-7 absolute left-0 top-0 h-full w-full bg-[#ddd]/75 backdrop-blur-[15px]">
                    <div className="max-h-screen-w/o-header absolute left-0 top-0 h-full w-full">
                        <div className="rounded-base xs:rounded-xl xs:p-10 absolute left-1/2 top-1/2 mx-auto flex w-full max-w-[calc(100%_-_50px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-5 bg-white p-5 sm:max-w-[508px] sm:gap-7 sm:rounded-2xl sm:p-16 lg:max-w-[540px] lg:rounded-3xl lg:p-20">
                            <div>
                                <h2 className="font-gibson mb-5 text-lg font-semibold sm:text-xl lg:text-3xl">
                                    {t("lock_title")}
                                </h2>
                                <p className="sm:text-md text-base lg:text-lg">{t("lock_description")}</p>
                            </div>
                            <form className="flex flex-col" onSubmit={handleSubmit(submit)}>
                                <WrapWithError error={error} id="code">
                                    <input
                                        {...register("code", {
                                            required: { value: true, message: t("form_error_required") },
                                        })}
                                        type="text"
                                        id="code"
                                        placeholder={t("lock_placeholder_code")}
                                        className={cn(
                                            "border-border-tide outline-hidden placeholder:text-lighter-grey focus:border-blue sm:text-md h-[58px] w-full rounded-full border px-[27px] text-base sm:h-[61px] sm:px-10",
                                            {
                                                "border-ui-red text-ui-red placeholder:text-ui-red focus:border-ui-red":
                                                    error,
                                            }
                                        )}
                                        required
                                        aria-invalid={!!error}
                                        aria-errormessage="err-code"
                                    />
                                </WrapWithError>
                                <button
                                    type="submit"
                                    className="bg-linear-to-r from-blue to-blue sm:text-md h-[58px] rounded-full via-[#0060a1] px-[27px] text-base font-semibold text-white sm:h-[61px]"
                                >
                                    {t("lock_button_submit")}
                                </button>
                            </form>
                            <Link
                                className="text-blue sm:text-md mx-auto mt-2 text-center text-base font-semibold underline sm:mt-0"
                                to="/"
                            >
                                {t("lock_button_skip")}
                            </Link>
                        </div>
                    </div>
                </div>
            )}
            {(!isAdmin || previewMode) && <PageViewer page={filteredPage} />}
        </div>
    )
}

export default Lock

Lock.schema = {
    name: "Lock",
    label: "Lock",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/Lock.png`,
}
